import React from 'react';
import { Box, Typography, Grid, Card, CardContent, Chip } from '@mui/material';
import { CheckCircle, Warning, Error as ErrorIcon, Schedule } from '@mui/icons-material';
import PublicLayout from '../components/layout/PublicLayout';

const services = [
  { name: 'Webmail', status: 'operational', uptime: '99.99%' },
  { name: 'SMTP (Outgoing)', status: 'operational', uptime: '99.99%' },
  { name: 'IMAP (Incoming)', status: 'operational', uptime: '99.99%' },
  { name: 'API', status: 'operational', uptime: '99.98%' },
  { name: 'Admin Panel', status: 'operational', uptime: '99.99%' },
  { name: 'DNS & Domain Verification', status: 'operational', uptime: '100%' },
];

const statusConfig: Record<string, { icon: React.ReactNode; color: string; label: string; bg: string }> = {
  operational: { icon: <CheckCircle sx={{ fontSize: 20 }} />, color: '#16a34a', label: 'Operational', bg: '#f0fdf4' },
  degraded: { icon: <Warning sx={{ fontSize: 20 }} />, color: '#d97706', label: 'Degraded', bg: '#fefce8' },
  outage: { icon: <ErrorIcon sx={{ fontSize: 20 }} />, color: '#dc2626', label: 'Outage', bg: '#fef2f2' },
  maintenance: { icon: <Schedule sx={{ fontSize: 20 }} />, color: '#2563eb', label: 'Maintenance', bg: '#eff6ff' },
};

const incidents = [
  { date: 'Mar 25, 2026', title: 'Scheduled Maintenance — Database Optimization', status: 'resolved', desc: 'Completed maintenance window for PostgreSQL performance optimization. No downtime occurred.', duration: '45 min' },
  { date: 'Mar 18, 2026', title: 'SSL Certificate Renewal', status: 'resolved', desc: 'Wildcard SSL certificate (*.cruvzmail.com) renewed via DNS-01 challenge. Zero-downtime deployment.', duration: '10 min' },
  { date: 'Mar 5, 2026', title: 'API Response Time Increase', status: 'resolved', desc: 'Brief increase in API response times due to high traffic. Load balancer auto-scaling resolved the issue.', duration: '15 min' },
];

const uptimeData = [
  { month: 'Jan 2026', uptime: 99.99 },
  { month: 'Feb 2026', uptime: 99.98 },
  { month: 'Mar 2026', uptime: 99.99 },
];

const StatusPage: React.FC = () => {
  const allOperational = services.every(s => s.status === 'operational');

  return (
    <PublicLayout>
      {/* Hero */}
      <Box sx={{
        pt: { xs: '130px', md: '140px' }, pb: { xs: 5, md: 6 }, px: { xs: 2, md: 5 },
        background: allOperational
          ? 'linear-gradient(135deg, #f0fdf4 0%, #fff 50%, #f0fdf4 100%)'
          : 'linear-gradient(135deg, #fef2f2 0%, #fff 50%, #fef2f2 100%)',
        textAlign: 'center',
      }}>
        <Box sx={{ maxWidth: 700, mx: 'auto' }}>
          <Box sx={{
            display: 'inline-flex', alignItems: 'center', gap: 1, px: 3, py: 1.5,
            borderRadius: '12px', bgcolor: allOperational ? '#f0fdf4' : '#fef2f2',
            border: `1px solid ${allOperational ? '#bbf7d0' : '#fecaca'}`, mb: 3,
          }}>
            <CheckCircle sx={{ fontSize: 24, color: allOperational ? '#16a34a' : '#dc2626' }} />
            <Typography sx={{ fontWeight: 700, color: allOperational ? '#16a34a' : '#dc2626', fontSize: '1rem' }}>
              {allOperational ? 'All Systems Operational' : 'Some Services Affected'}
            </Typography>
          </Box>
          <Typography variant="h2" sx={{ fontWeight: 800, fontSize: { xs: '2rem', md: '2.8rem' }, color: '#0f172a', mb: 1, lineHeight: 1.2 }}>
            System Status
          </Typography>
          <Typography sx={{ fontSize: '0.9rem', color: '#94a3b8' }}>
            Last updated: {new Date().toLocaleString()}
          </Typography>
        </Box>
      </Box>

      {/* Services */}
      <Box sx={{ py: { xs: 4, md: 6 }, px: { xs: 2, md: 5 }, bgcolor: '#fff' }}>
        <Box sx={{ maxWidth: 800, mx: 'auto' }}>
          <Box sx={{ bgcolor: '#f8fafc', borderRadius: 3, overflow: 'hidden', border: '1px solid #e2e8f0' }}>
            {services.map((svc, i) => {
              const cfg = statusConfig[svc.status];
              return (
                <Box key={svc.name} sx={{
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  px: 3, py: 2.5, borderBottom: i < services.length - 1 ? '1px solid #e2e8f0' : 'none',
                  bgcolor: '#fff',
                }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Box sx={{ color: cfg.color }}>{cfg.icon}</Box>
                    <Typography sx={{ fontWeight: 600, color: '#0f172a', fontSize: '0.95rem' }}>{svc.name}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Typography sx={{ fontSize: '0.8rem', color: '#94a3b8', display: { xs: 'none', sm: 'block' } }}>{svc.uptime} uptime</Typography>
                    <Chip label={cfg.label} size="small" sx={{ bgcolor: cfg.bg, color: cfg.color, fontWeight: 600, fontSize: '0.7rem' }} />
                  </Box>
                </Box>
              );
            })}
          </Box>
        </Box>
      </Box>

      {/* Uptime History */}
      <Box sx={{ py: { xs: 4, md: 6 }, px: { xs: 2, md: 5 }, bgcolor: '#f8fafc' }}>
        <Box sx={{ maxWidth: 800, mx: 'auto' }}>
          <Typography variant="h5" sx={{ fontWeight: 800, mb: 4, color: '#0f172a' }}>Uptime History</Typography>
          <Grid container spacing={3}>
            {uptimeData.map((d) => (
              <Grid size={{ xs: 12, sm: 4 }} key={d.month}>
                <Card elevation={0} sx={{ borderRadius: 3, border: '1px solid #e2e8f0', textAlign: 'center' }}>
                  <CardContent sx={{ p: 3 }}>
                    <Typography sx={{ fontSize: '2rem', fontWeight: 900, color: d.uptime >= 99.99 ? '#16a34a' : '#d97706' }}>{d.uptime}%</Typography>
                    <Typography sx={{ fontSize: '0.85rem', color: '#64748b', fontWeight: 500 }}>{d.month}</Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>
      </Box>

      {/* Recent Incidents */}
      <Box sx={{ py: { xs: 6, md: 8 }, px: { xs: 2, md: 5 }, bgcolor: '#fff' }}>
        <Box sx={{ maxWidth: 800, mx: 'auto' }}>
          <Typography variant="h5" sx={{ fontWeight: 800, mb: 4, color: '#0f172a' }}>Recent Incidents</Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {incidents.map((inc) => (
              <Card key={inc.title} elevation={0} sx={{ borderRadius: 3, border: '1px solid #e2e8f0' }}>
                <CardContent sx={{ p: 3 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                    <Typography sx={{ fontWeight: 700, color: '#0f172a', fontSize: '0.95rem' }}>{inc.title}</Typography>
                    <Chip label="Resolved" size="small" sx={{ bgcolor: '#f0fdf4', color: '#16a34a', fontWeight: 600, fontSize: '0.7rem' }} />
                  </Box>
                  <Typography sx={{ fontSize: '0.85rem', color: '#64748b', lineHeight: 1.6, mb: 1.5 }}>{inc.desc}</Typography>
                  <Box sx={{ display: 'flex', gap: 3, color: '#94a3b8', fontSize: '0.75rem' }}>
                    <Typography sx={{ fontSize: 'inherit' }}>{inc.date}</Typography>
                    <Typography sx={{ fontSize: 'inherit' }}>Duration: {inc.duration}</Typography>
                  </Box>
                </CardContent>
              </Card>
            ))}
          </Box>
        </Box>
      </Box>
    </PublicLayout>
  );
};

export default StatusPage;
